/**
 * Operations: what the jobs did, and whether the mail went out.
 *
 * The two questions this screen exists to answer are the two that get asked when something is
 * wrong and nobody can see why:
 *
 *   1. **"Did the confirmation email actually send?"** — the outbox, with the status, the attempt
 *      count and the last error. A `parked` row is the interesting one: mail nobody could send.
 *   2. **"Is anything still running?"** — the job table, with when each one last ran. A job that
 *      silently stopped firing is the failure mode that actually happens, and it is invisible
 *      unless something states the last run time out loud. A registered job with no runs at all
 *      shows as *never*, which is the case worth catching.
 *
 * Run is here for the same reason: when mail is stuck, draining the outbox and reading the error
 * beats waiting thirty seconds and guessing.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { formatInstant } from '@/lib/dates';
import { Alert, Badge, Button, Card, EmptyState, Table, Td, Th } from '@/components/ui';
import { ErrorNote, PageHeader } from '@/components/bits';
import type { Tone } from '@/lib/policy';
import type { JobOutcome, OutboxStatus } from '@/lib/types';

const OUTCOME_TONE: Record<JobOutcome, Tone> = {
  ok: 'good',
  failed: 'bad',
  skipped: 'neutral',
  locked_out: 'neutral',
};

const STATUS_TONE: Record<OutboxStatus, Tone> = {
  sent: 'good',
  pending: 'progress',
  failed: 'warn',
  parked: 'bad',
};

/** "4 minutes ago" beats a timestamp for the one question this column answers. */
function ago(seconds: number): string {
  if (seconds < 90) return `${Math.round(seconds)}s ago`;
  const minutes = seconds / 60;
  if (minutes < 90) return `${Math.round(minutes)} min ago`;
  const hours = minutes / 60;
  if (hours < 36) return `${Math.round(hours)} h ago`;
  return `${Math.round(hours / 24)} days ago`;
}

export function HrOperations() {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<OutboxStatus | 'all'>('all');
  const [ran, setRan] = useState<string | null>(null);
  const [noops, setNoops] = useState(false);

  // The drain runs every thirty seconds and usually has nothing to send, so its rows are hidden
  // unless asked for — otherwise they are the whole table and the interesting runs scroll away.
  const jobs = useQuery({
    queryKey: ['jobs', noops],
    queryFn: () => api.jobs({ limit: 40, include_noops: noops }),
  });
  const outbox = useQuery({
    queryKey: ['outbox', status],
    queryFn: () => api.outbox(status === 'all' ? { limit: 50 } : { status, limit: 50 }),
  });

  const run = useMutation({
    mutationFn: (job: string) => api.runJob(job),
    onSuccess: (result) => {
      setRan(
        result.outcome === 'ok'
          ? `${result.job} ran in ${result.duration_ms} ms — ${describe(result.detail)}`
          : `${result.job}: ${result.outcome}${result.error ? ` — ${result.error}` : ''}`,
      );
      void queryClient.invalidateQueries({ queryKey: ['jobs'] });
      void queryClient.invalidateQueries({ queryKey: ['outbox'] });
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const latest = new Map((jobs.data?.latest ?? []).map((row) => [row.job, row]));
  const counts = outbox.data?.counts ?? {};

  return (
    <div className="space-y-6">
      <PageHeader
        title="Operations"
        subtitle="The scheduled jobs and the email outbox — evidence, rather than assurances."
      />

      {ran ? <Alert tone="neutral">{ran}</Alert> : null}
      {run.error ? <ErrorNote error={run.error} /> : null}

      <Card
        title={
          <CardTitle
            title="Scheduled jobs"
            note="Every job this build registers, when it runs, and when it last did."
          />
        }
      >
        {jobs.error ? <ErrorNote error={jobs.error} /> : null}
        <Table>
          <thead>
            <tr>
              <Th>Job</Th>
              <Th>Schedule</Th>
              <Th>Last run</Th>
              <Th>Outcome</Th>
              <Th numeric>Took</Th>
              <Th />
            </tr>
          </thead>
          <tbody>
            {(jobs.data?.registered ?? []).map((job) => {
              const last = latest.get(job.job);
              return (
                <tr key={job.job}>
                  <Td>
                    <span className="font-medium">{job.job}</span>
                    <span className="text-muted-foreground block text-xs">{job.description}</span>
                  </Td>
                  <Td>
                    <span className="font-mono text-xs">{job.schedule}</span>
                  </Td>
                  <Td>
                    {last ? (
                      <>
                        {ago(last.seconds_ago)}
                        <span className="text-muted-foreground block text-xs">
                          {formatInstant(last.last_run_at)}
                        </span>
                      </>
                    ) : (
                      <Badge tone="warn">never</Badge>
                    )}
                  </Td>
                  <Td>
                    {last?.last_outcome ? (
                      <Badge tone={OUTCOME_TONE[last.last_outcome]}>{last.last_outcome}</Badge>
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td numeric>
                    {last?.duration_ms === null ? '—' : `${last?.duration_ms ?? 0} ms`}
                  </Td>
                  <Td>
                    <Button
                      size="sm"
                      busy={run.isPending && run.variables === job.job}
                      onClick={() => run.mutate(job.job)}
                    >
                      Run now
                    </Button>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </Card>

      <Card
        title={
          <CardTitle
            title="Recent runs"
            note="Runs that did something, plus every failure. Hand-runs say who asked."
          />
        }
        action={
          <Button
            size="sm"
            variant={noops ? 'primary' : 'secondary'}
            onClick={() => setNoops((was) => !was)}
          >
            {noops ? 'Hide quiet runs' : 'Show quiet runs'}
          </Button>
        }
      >
        {(jobs.data?.runs ?? []).length === 0 ? (
          <EmptyState title="No job has run on this instance yet.">
            The scheduler ticks every twenty seconds; the outbox drain is the first thing that will
            appear here.
          </EmptyState>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Started</Th>
                <Th>Job</Th>
                <Th>Outcome</Th>
                <Th>Detail</Th>
                <Th>Triggered by</Th>
              </tr>
            </thead>
            <tbody>
              {(jobs.data?.runs ?? []).map((row) => (
                <tr key={row.id}>
                  <Td>{formatInstant(row.started_at)}</Td>
                  <Td>{row.job}</Td>
                  <Td>
                    {row.outcome ? (
                      <Badge tone={OUTCOME_TONE[row.outcome]}>{row.outcome}</Badge>
                    ) : (
                      <Badge tone="progress">running</Badge>
                    )}
                  </Td>
                  <Td>
                    <span className="text-muted-foreground text-xs">
                      {row.error ?? describe(row.detail)}
                    </span>
                  </Td>
                  <Td>{row.triggered_by}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>

      <Card
        title={
          <CardTitle
            title="Email outbox"
            note="Nothing is sent inline: a workflow queues a row here, and the drain sends it."
          />
        }
      >
        <div className="mb-3 flex flex-wrap items-center gap-2">
          {(['all', 'pending', 'sent', 'failed', 'parked'] as const).map((option) => (
            <Button
              key={option}
              size="sm"
              variant={status === option ? 'primary' : 'secondary'}
              onClick={() => setStatus(option)}
            >
              {option}
              {option !== 'all' && counts[option] !== undefined ? ` (${counts[option]})` : ''}
            </Button>
          ))}
        </div>

        {counts.parked ? (
          <Alert tone="bad">
            {counts.parked} message{counts.parked === 1 ? '' : 's'} could not be sent after six
            attempts. The text is still here — fix the cause and the row can be re-queued rather
            than re-typed.
          </Alert>
        ) : null}

        {outbox.error ? <ErrorNote error={outbox.error} /> : null}
        {(outbox.data?.messages ?? []).length === 0 ? (
          <EmptyState title="Nothing in the outbox." />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Queued</Th>
                <Th>To</Th>
                <Th>Subject</Th>
                <Th>Status</Th>
                <Th numeric>Tries</Th>
                <Th>Last error</Th>
              </tr>
            </thead>
            <tbody>
              {(outbox.data?.messages ?? []).map((message) => (
                <tr key={message.id}>
                  <Td>{formatInstant(message.created_at)}</Td>
                  <Td>
                    <span className="text-xs">{message.to_addresses.join(', ')}</span>
                    {message.cc_addresses.length > 0 ? (
                      <span className="text-muted-foreground block text-xs">
                        cc {message.cc_addresses.join(', ')}
                      </span>
                    ) : null}
                  </Td>
                  <Td>
                    {message.subject}
                    <span className="text-muted-foreground block font-mono text-[0.7rem]">
                      {message.template}
                    </span>
                  </Td>
                  <Td>
                    <Badge tone={STATUS_TONE[message.status]}>{message.status}</Badge>
                    {message.sent_at ? (
                      <span className="text-muted-foreground block text-xs">
                        {formatInstant(message.sent_at)}
                      </span>
                    ) : null}
                  </Td>
                  <Td numeric>{message.attempts}</Td>
                  <Td>
                    <span className="text-muted-foreground text-xs">
                      {message.last_error ?? '—'}
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}

/** A job's detail is free-form JSON; this makes one line of it without pretending to a schema. */
function describe(detail: Record<string, unknown>): string {
  const entries = Object.entries(detail).filter(([, value]) => value !== null && value !== 0);
  if (entries.length === 0) return 'nothing to do';
  return entries
    .slice(0, 6)
    .map(
      ([key, value]) =>
        `${key}: ${typeof value === 'object' ? JSON.stringify(value) : String(value)}`,
    )
    .join(', ');
}

/** A card title with a line of context under it, which `Card` accepts as a node. */
function CardTitle({ title, note }: { title: string; note: string }) {
  return (
    <span className="block">
      {title}
      <span className="text-muted-foreground block text-xs font-normal">{note}</span>
    </span>
  );
}
